import * as React from 'react';
import { DealerSalesYear } from '../../../../support/models/dealers/dealer-sales';
import { formatCurrency } from '../../../../utils/currency-formatter';

interface IProps {
    year: DealerSalesYear;
}

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A colour for each division that stays the same whichever divisions a year has. */
export const DIVISION_COLORS: { [division: string]: string } = {
    CD: '#2a78d6',
    RS: '#eb6834',
    OEM: '#1baf7a',
    PMAX: '#eda100'
};

export const DIVISION_NAMES: { [division: string]: string } = {
    CD: 'Complete Door',
    RS: 'Rolling Steel',
    OEM: 'OEM',
    PMAX: 'Performax'
};

const WIDTH = 720;
const HEIGHT = 260;
const PLOT = { left: 44, right: 8, top: 10, bottom: 24 };
const BAR_WIDTH = 24;
const GAP = 2;
const RADIUS = 4;

/** A step of 1, 2 or 5 times a power of ten that puts about four ticks under the biggest value. */
export const niceStep = (max: number) => {
    const rough = Math.max(max, 1) / 4;
    const power = Math.pow(10, Math.floor(Math.log10(rough)));
    const fraction = rough / power;
    return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * power;
};

export const formatAxis = (value: number) => {
    if (value >= 1000000) {
        return `$${parseFloat((value / 1000000).toFixed(2))}M`;
    }
    return value >= 1000 ? `$${parseFloat((value / 1000).toFixed(1))}k` : `$${value}`;
};

/** A bar with its top corners rounded and its bottom square. */
const topRounded = (x: number, y: number, w: number, h: number, r: number) => {
    const radius = Math.min(r, h, w / 2);
    return `M${x},${y + h} V${y + radius} Q${x},${y} ${x + radius},${y} H${x + w - radius} Q${x + w},${y} ${x + w},${y + radius} V${y + h} Z`;
};

/**
 * The sales of a year in columns, a column for each month, stacked by the division that invoiced it. A month with a
 * credit that outweighs its sales shows nothing in the column, and says so in the figures it shows on hover.
 */
export const DealerSalesChart = (props: IProps) => {

    const { year } = props;
    const [hovered, setHovered] = React.useState<number | undefined>(undefined);

    const plotWidth = WIDTH - PLOT.left - PLOT.right;
    const plotHeight = HEIGHT - PLOT.top - PLOT.bottom;
    const slot = plotWidth / 12;

    const columnTotals = MONTHS.map((_, m) =>
        year.divisions.reduce((sum, d) => sum + Math.max(d.months[m] ?? 0, 0), 0));
    const step = niceStep(Math.max(...columnTotals));
    const top = Math.ceil(Math.max(...columnTotals, 1) / step) * step;
    const y = (value: number) => PLOT.top + plotHeight - (value / top) * plotHeight;
    const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);

    const summary = `Sales of ${year.year} by month and division, ${formatCurrency(year.total)} in all.`;

    const tooltip = hovered === undefined ? undefined : {
        month: MONTHS[hovered],
        rows: year.divisions
            .filter(d => d.months[hovered] !== null)
            .map(d => ({ division: d.division, amount: d.months[hovered] as number })),
        total: year.months[hovered]
    };

    return <div className="dealer-sales-chart">
        <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            role="img"
            aria-label={summary}
            onMouseLeave={() => setHovered(undefined)}>
            <title>{summary}</title>
            {
                ticks.map(tick => (
                    <g key={tick}>
                        <line className="dealer-sales-grid" x1={PLOT.left} x2={WIDTH - PLOT.right} y1={y(tick)} y2={y(tick)} />
                        <text className="dealer-sales-axis" x={PLOT.left - 8} y={y(tick)} textAnchor="end" dominantBaseline="middle">
                            {formatAxis(tick)}
                        </text>
                    </g>
                ))
            }
            {
                MONTHS.map((month, m) => {
                    const cx = PLOT.left + slot * m + slot / 2;
                    const x = cx - BAR_WIDTH / 2;
                    const segments = year.divisions
                        .map(d => ({ division: d.division, value: Math.max(d.months[m] ?? 0, 0) }))
                        .filter(segment => segment.value > 0);
                    let stacked = 0;

                    return <g key={month}>
                        {
                            segments.map((segment, i) => {
                                const bottom = y(stacked);
                                stacked += segment.value;
                                const topY = y(stacked);
                                const isTop = i === segments.length - 1;
                                // each segment stops short of the one above it, so a gap of the surface shows between them
                                const height = Math.max(bottom - topY - (isTop ? 0 : GAP), 1);
                                const color = DIVISION_COLORS[segment.division] ?? '#898781';

                                return isTop
                                    ? <path key={segment.division} d={topRounded(x, topY, BAR_WIDTH, height, RADIUS)} fill={color} />
                                    : <rect key={segment.division} x={x} y={bottom - height} width={BAR_WIDTH} height={height} fill={color} />;
                            })
                        }
                        <text className="dealer-sales-axis" x={cx} y={HEIGHT - 7} textAnchor="middle">{month}</text>
                        <rect
                            className="dealer-sales-hit"
                            x={PLOT.left + slot * m}
                            y={PLOT.top}
                            width={slot}
                            height={plotHeight + PLOT.bottom}
                            onMouseEnter={() => setHovered(m)} />
                    </g>;
                })
            }
            <line className="dealer-sales-baseline" x1={PLOT.left} x2={WIDTH - PLOT.right} y1={y(0)} y2={y(0)} />
        </svg>
        {
            tooltip && hovered !== undefined ?
                <div
                    className="dealer-sales-tooltip"
                    style={{
                        left: `${((PLOT.left + slot * hovered + slot / 2) / WIDTH) * 100}%`,
                        transform: hovered > 8 ? 'translateX(-105%)' : 'translateX(5%)'
                    }}>
                    <strong>{tooltip.month} {year.year}</strong>
                    {
                        tooltip.rows.length === 0 ? <div>No sales</div> :
                            <React.Fragment>
                                {
                                    tooltip.rows.map(row => (
                                        <div key={row.division} className="dealer-sales-tooltip-row">
                                            <span className="dealer-sales-swatch" style={{ background: DIVISION_COLORS[row.division] }} />
                                            <span>{DIVISION_NAMES[row.division] ?? row.division}</span>
                                            <span>{formatCurrency(row.amount)}</span>
                                        </div>
                                    ))
                                }
                                {
                                    tooltip.rows.length > 1 ?
                                        <div className="dealer-sales-tooltip-row dealer-sales-tooltip-total">
                                            <span /><span>Total</span><span>{formatCurrency(tooltip.total ?? 0)}</span>
                                        </div>
                                        : null
                                }
                            </React.Fragment>
                    }
                </div>
                : null
        }
    </div>;

};
